/**
 * Attorney citation-review queue for the Law tab.
 *
 * Every entry in the curated CASELAW database has already been checked
 * against real sources by the app (see the 2026-07-24 validity audits in
 * memory: cases.json and caselaw.ts both had confirmed miscitations/
 * fabrications found and fixed that same session). That AI-verification
 * pass is not a substitute for the attorney's own professional-responsibility
 * check before relying on a citation in court — this module tracks that
 * separate, human sign-off per authority, independent of the AI pass.
 *
 * Case-law entries carry AI-drafted holdings/summaries and are the class
 * that produced every confirmed error in both audits; court rules are
 * curated summaries of a real rule number; statutes are direct G.L.
 * citations with the least interpretive risk. That ordering drives the
 * default risk ranking below — not a claim that any specific entry is
 * currently wrong, just where review time is best spent first.
 */
import type { CaselawEntry } from '@/types';
import { CASELAW } from '@/data/caselaw';
import { byId } from '@/ui/dom';
import { showToast } from '@/ui/overlays';

type ReviewStatus = 'confirmed' | 'flagged';
interface ReviewRecord {
  status: ReviewStatus;
  at: string;
  note?: string;
}
type ReviewState = Record<string, ReviewRecord>;

const STORAGE_KEY = 'voirai_citation_review';

function loadState(): ReviewState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as ReviewState) : {};
  } catch {
    return {};
  }
}

function saveState(): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* storage unavailable — review state just won't persist across reloads */
  }
}

let state = loadState();

function escH(s: string): string {
  return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/** Case law > rule > statute — see module doc comment for why. */
function riskRank(e: CaselawEntry): number {
  return e.type === 'caselaw' ? 2 : e.type === 'rule' ? 1 : 0;
}
function riskLabel(rank: number): string {
  return rank === 2 ? 'Higher risk' : rank === 1 ? 'Medium risk' : 'Lower risk';
}

export function confirmCitation(id: string): void {
  state[id] = { status: 'confirmed', at: new Date().toISOString() };
  saveState();
  showToast('Marked attorney-confirmed');
  renderCitationReview();
}

export function flagCitation(id: string): void {
  const existingNote = state[id]?.note || '';
  state[id] = { status: 'flagged', at: new Date().toISOString(), note: existingNote };
  saveState();
  showToast('Flagged for correction');
  renderCitationReview();
}

export function unreviewCitation(id: string): void {
  delete state[id];
  saveState();
  renderCitationReview();
}

export function setCitationNote(id: string, note: string): void {
  const rec = state[id];
  if (!rec) return;
  rec.note = note;
  saveState();
}

export function renderCitationReview(): void {
  const container = byId('citation-review');
  if (!container) return;

  const total = CASELAW.length;
  const confirmed = CASELAW.filter((e) => state[e.id]?.status === 'confirmed').length;
  const flagged = CASELAW.filter((e) => state[e.id]?.status === 'flagged').length;
  const pct = total ? Math.round((confirmed / total) * 1000) / 10 : 0;

  // Priority: flagged (unresolved problem) > unreviewed (needs a first look) > confirmed (done).
  const priority = (id: string): number => {
    const status = state[id]?.status;
    return status === 'flagged' ? 0 : status === 'confirmed' ? 2 : 1;
  };
  const ranked = CASELAW.slice().sort((a, b) => {
    const p = priority(a.id) - priority(b.id);
    if (p !== 0) return p;
    return riskRank(b) - riskRank(a); // then highest risk first
  });

  let html =
    '<div style="background:#f1f5f9;border:1px solid #e2e8f0;border-radius:10px;padding:11px 12px;margin-bottom:12px;">';
  html +=
    '<div style="font-size:10px;font-weight:700;color:#475569;text-transform:uppercase;letter-spacing:.4px;margin-bottom:7px;">Attorney citation review</div>';
  html += `<div style="font-size:14px;font-weight:700;color:#1e293b;margin-bottom:2px;">${confirmed} / ${total} authorities attorney-confirmed (${pct}%)</div>`;
  if (flagged) {
    html += `<div style="font-size:11px;color:#b45309;font-weight:600;margin-bottom:6px;">${flagged} flagged for correction</div>`;
  }
  html +=
    '<p style="font-size:11px;color:#64748b;margin:0 0 9px;line-height:1.6;">Every authority below has already been checked against real sources by the app. Confirming here records that you personally reviewed it before relying on it in court. Not-yet-reviewed and higher-risk authorities are listed first.</p>';

  html += '<div style="max-height:340px;overflow-y:auto;display:flex;flex-direction:column;gap:6px;">';
  ranked.forEach((e) => {
    const rec = state[e.id];
    const rank = riskRank(e);
    const badgeColor =
      rec?.status === 'confirmed'
        ? '#16a34a'
        : rec?.status === 'flagged'
          ? '#dc2626'
          : rank === 2
            ? '#b45309'
            : rank === 1
              ? '#7c3aed'
              : '#64748b';
    const badgeLabel =
      rec?.status === 'confirmed' ? 'Confirmed' : rec?.status === 'flagged' ? 'Flagged' : riskLabel(rank);
    html += '<div style="background:#fff;border:1px solid #e2e8f0;border-radius:7px;padding:7px 9px;">';
    html += '<div style="display:flex;align-items:center;gap:8px;">';
    html += `<span style="flex-shrink:0;background:${badgeColor}22;color:${badgeColor};border-radius:6px;padding:2px 7px;font-size:9px;font-weight:700;text-transform:uppercase;">${badgeLabel}</span>`;
    html += `<span style="flex:1;min-width:0;font-size:11px;color:#1e293b;font-weight:600;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;" title="${escH(e.citation)}">${escH(e.citation)}</span>`;
    if (!rec || rec.status === 'flagged') {
      html += `<button onclick="confirmCitation('${e.id}')" title="Mark reviewed and accurate" style="flex-shrink:0;background:#16a34a;color:#fff;border:none;border-radius:5px;padding:4px 8px;font-size:10px;font-weight:700;cursor:pointer;">✓ Confirm</button>`;
    }
    if (!rec || rec.status === 'confirmed') {
      html += `<button onclick="flagCitation('${e.id}')" title="Flag as wrong" style="flex-shrink:0;background:#fef2f2;color:#dc2626;border:1px solid #fecaca;border-radius:5px;padding:4px 8px;font-size:10px;font-weight:700;cursor:pointer;">⚠ Flag</button>`;
    }
    if (rec) {
      html += `<button onclick="unreviewCitation('${e.id}')" title="Reset to unreviewed" style="flex-shrink:0;background:none;color:#94a3b8;border:none;font-size:14px;cursor:pointer;line-height:1;padding:0 2px;">×</button>`;
    }
    html += '</div>';
    if (rec?.status === 'flagged') {
      html += `<textarea placeholder="What looks wrong with this citation?" onblur="setCitationNote('${e.id}', this.value)" style="width:100%;margin-top:6px;font-size:11px;color:#991b1b;border:1px solid #fecaca;border-radius:5px;padding:5px 7px;resize:vertical;min-height:32px;font-family:inherit;">${escH(rec.note || '')}</textarea>`;
    }
    html += '</div>';
  });
  html += '</div></div>';

  container.innerHTML = html;
}
